/**
 * Shared document-totals math used by both the API server and the React SPA.
 *
 * The single source of truth: total = (subtotal − discount) + tax. Tax is
 * applied on the post-discount base. Profit and margin use the post-discount,
 * pre-tax revenue base so taxes don't distort the contractor's earnings.
 *
 * Every site that displays or stores a calculated total — backend routes,
 * email templates, PDF generation, frontend previews — must funnel through
 * `computeDocumentTotals` so the numbers reconcile.
 */

/**
 * Shared tax-label shape. `name` and `rate` drive totals; `externalSource` and
 * `externalId` link the label to a connected accounting integration's tax code
 * (e.g. QuickBooks). `taxId` is the legacy free-form tax registration number
 * shown on PDFs.
 */
export type TaxLine = {
  name: string;
  rate: number;
  taxId?: string | null;
  externalSource?: "quickbooks" | "xero" | null;
  externalId?: string | null;
};

export function parseSelectedTaxLines(raw: string | null | undefined): TaxLine[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((t: unknown): t is TaxLine => {
      if (typeof t !== "object" || t === null) return false;
      const obj = t as Record<string, unknown>;
      return typeof obj.name === "string" && typeof obj.rate === "number";
    });
  } catch { return []; }
}

/**
 * A document's `selectedTaxLines` is a snapshot taken when the line was ticked,
 * so a tax registration number edited in Settings afterwards leaves existing
 * documents — and every PDF generated from them — showing the old number.
 * Registration numbers identify the business, not the document, so re-read
 * `taxId` from the current settings lines (matched on name + rate, the same key
 * the selection checkboxes use) whenever a label is rendered. Rates and names
 * stay snapshotted: those drive the math and must not shift under a sent
 * document. Lines with no settings match keep whatever they stored.
 */
export function reconcileTaxLineIds(
  selected: TaxLine[],
  settingsTaxLines: string | null | undefined,
): TaxLine[] {
  const settings = parseSelectedTaxLines(settingsTaxLines);
  if (settings.length === 0) return selected;
  return selected.map(line => {
    const match = settings.find(s => s.name === line.name && s.rate === line.rate);
    if (!match) return line;
    return { ...line, taxId: match.taxId || undefined };
  });
}

export type DiscountType = "fixed" | "percent";

/**
 * Compute the dollar amount of a document-level discount.
 * Returns 0 if discount fields are not set or invalid.
 * Caps the discount at the subtotal so the discounted base never goes negative.
 */
export function computeDiscountAmount(
  subtotal: number,
  discountType: string | null | undefined,
  discountValue: number | null | undefined,
): number {
  if (discountType == null || discountValue == null) return 0;
  if (!Number.isFinite(discountValue) || discountValue <= 0) return 0;
  const raw = discountType === "percent"
    ? subtotal * (discountValue / 100)
    : discountValue;
  return Math.max(0, Math.min(raw, subtotal));
}

export function computeTaxAmount(subtotal: number, taxEnabled: boolean, taxRate: number, selectedTaxLines?: string | null): number {
  if (!taxEnabled) return 0;
  // When selectedTaxLines is explicitly set (even as "[]"), use the parsed
  // array — an empty array means $0 tax (user cleared all lines).
  // Only fall back to taxRate when selectedTaxLines was never configured.
  if (selectedTaxLines != null && selectedTaxLines !== "") {
    return parseSelectedTaxLines(selectedTaxLines).reduce((sum, tl) => sum + subtotal * (tl.rate / 100), 0);
  }
  return subtotal * (taxRate / 100);
}

/**
 * Compute the totals breakdown for a document. Tax is applied on the
 * post-discount base. Total = subtotal - discount + tax.
 */
export function computeDocumentTotals(args: {
  subtotal: number;
  discountType?: string | null;
  discountValue?: number | null;
  taxEnabled: boolean;
  taxRate: number;
  selectedTaxLines?: string | null;
}): { discountAmount: number; taxableBase: number; taxAmount: number; total: number } {
  const discountAmount = computeDiscountAmount(args.subtotal, args.discountType, args.discountValue);
  const taxableBase = args.subtotal - discountAmount;
  const taxAmount = computeTaxAmount(taxableBase, args.taxEnabled, args.taxRate, args.selectedTaxLines);
  const total = taxableBase + taxAmount;
  return { discountAmount, taxableBase, taxAmount, total };
}

/**
 * Compute profit and margin for a document. The base is the post-discount,
 * pre-tax revenue (`subtotal − discountAmount`) so taxes don't distort the
 * contractor's actual earnings. Margin is 0 when the revenue base is 0 or
 * negative to avoid divide-by-zero / nonsense numbers.
 */
export function computeMarginAndProfit(args: {
  subtotal: number;
  discountAmount: number;
  internalCost: number;
}): { revenueBase: number; profit: number; margin: number } {
  const revenueBase = args.subtotal - args.discountAmount;
  const profit = revenueBase - args.internalCost;
  const margin = revenueBase > 0 ? (profit / revenueBase) * 100 : 0;
  return { revenueBase, profit, margin };
}

/**
 * Target margin: the margin (%) a contractor aims for, set in Settings
 * (company_settings.target_margin). Every margin shown in the app is coloured
 * against it, so the rule lives here once:
 *   good  (green)  at or above the target
 *   thin  (amber)  below the target but at least half of it
 *   low   (red)    below half the target, or negative
 * With the default of 30 that's the 30% / 15% split the line-item profit
 * chips have always used.
 */
export const DEFAULT_TARGET_MARGIN = 30;
export type MarginTone = "good" | "thin" | "low";

export function marginTone(margin: number, target: number = DEFAULT_TARGET_MARGIN): MarginTone {
  if (margin >= target) return "good";
  if (margin >= 0 && margin >= target / 2) return "thin";
  return "low";
}

/**
 * How much the pre-tax revenue (after discount) has to rise for the margin to
 * reach the target with the same costs: cost ÷ (1 − target) − revenue.
 * 0 when the margin is already there; null when no price can reach it
 * (a target of 100% or more).
 */
export function priceToReachTarget(revenueBase: number, internalCost: number, target: number): number | null {
  if (target >= 100) return null;
  const needed = internalCost / (1 - target / 100);
  return Math.max(0, needed - revenueBase);
}
